/**
 * Brand loader (SEC-334 brand administration)
 *
 * Asks the API which brand belongs to the web address this portal is loaded on
 * and keeps the answer in browser storage for an instant paint next time.
 * Also loads the brand list for the admin dropdowns.
 */
import axios from "axios";
import router from "@/router";
import { getBrand, setBrandFromApi, applyBrandToDocument, applyBrandStartingLanguage, clearStaleBrandSession, hasCachedBrand, BRAND_CACHE_KEY, DEV_HOST_OVERRIDE_KEY } from "@/utils/brand";

// A first visit waits this long for the brand before the app is shown anyway,
// so a slow or unreachable API can never leave the visitor with a blank page.
const FIRST_PAINT_TIMEOUT_MS = 2500;

// Both answers are cached, the partner brand and Securdia. Browser storage is per web
// address, so the next visit to this address paints the right brand immediately.
function writeCache(payload) {
  try {
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(payload || { key: "securdia", isDefault: true }));
  } catch (e) {
    // storage blocked, ignore
  }
}

function devHostOverride() {
  if (process.env.NODE_ENV === "production") return "";
  try {
    return window.localStorage.getItem(DEV_HOST_OVERRIDE_KEY) || "";
  } catch (e) {
    return "";
  }
}

/**
 * Fetch the brand for this web address and apply it. Runs at start-up; the
 * cached brand (or Securdia) is already showing while this is in flight.
 */
export async function refreshBrandFromApi() {
  try {
    const params = {};
    const host = devHostOverride();
    if (host) params.host = host;

    const { data } = await axios.get(`${process.env.VUE_APP_SERVER_URL}/brand/current`, { params });
    const brand = setBrandFromApi(data);
    writeCache(data);
    applyBrandToDocument(brand);
    applyBrandStartingLanguage(brand); // SEC-334: a first visit has no cached brand to start from

    // A stored login from another brand ends here, on this brand's login page.
    // On a first visit this runs before the app is mounted; the router is not attached to an
    // app yet, so the push is skipped and the route guard sends the visitor to /login instead.
    if (clearStaleBrandSession(brand) && router.app) router.push("/login").catch(() => {});
  } catch (error) {
    // A refused origin is a definite answer, not an outage: this address belongs to no brand
    // right now. Whatever was cached for it is stale (a brand since removed, or one whose
    // portal address no longer matches), and keeping it would show a broken logo from paths
    // that no longer exist. Drop it and show Securdia. A network error keeps the cache.
    const refused = error.response && error.response.data && error.response.data.errorId === "origin_not_allowed";
    if (refused) {
      try { window.localStorage.removeItem(BRAND_CACHE_KEY); } catch (e) { /* storage blocked */ }
      const brand = setBrandFromApi({ key: "securdia", isDefault: true });
      applyBrandToDocument(brand);
      if (clearStaleBrandSession(brand) && router.app) router.push("/login").catch(() => {});
    }

    // The usual cause is a refused origin: the portal address in the reseller's branding
    // does not match the address this portal is served on, so the API rejects every call.
    console.warn(
      `Brand: could not load the brand for ${window.location.origin}, keeping the current one. ` +
        "If this is a partner domain, check that the portal address in its reseller's branding matches this address exactly. " +
        `(${error.message})`
    );
  }
  return getBrand();
}

/**
 * Start loading the brand and say when the app may be shown.
 *
 * Been here before: the cached brand is already on screen, so show the app at once
 * and let the fresh answer land in the background.
 * First visit: wait for the answer, so the page never paints one brand and then
 * swaps to another. Capped by FIRST_PAINT_TIMEOUT_MS.
 */
export function startBrandLoad() {
  const loading = refreshBrandFromApi();

  if (hasCachedBrand()) return Promise.resolve();

  const timeout = new Promise((resolve) => setTimeout(resolve, FIRST_PAINT_TIMEOUT_MS));
  return Promise.race([loading, timeout]);
}
